/**
 * Portfolio Update Hooks
 * TanStack Query hooks for portfolio updates with cursor-based pagination
 */

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { portfolioUpdateRepo } from '@/lib/repositories'
import type { CreatePortfolioUpdateData } from '@/lib/repositories'
import { FEED_PAGE_SIZE } from '@/lib/types/pagination'

export const PORTFOLIO_UPDATES_KEY = ['portfolio-updates'] as const

export function usePortfolioUpdates() {
  return useInfiniteQuery({
    queryKey: [...PORTFOLIO_UPDATES_KEY],
    queryFn: ({ pageParam }) =>
      portfolioUpdateRepo.getUpdatesPaginated({
        cursor: pageParam?.cursor,
        cursorId: pageParam?.cursorId,
        limit: FEED_PAGE_SIZE,
      }),
    initialPageParam: undefined as
      | { cursor: string; cursorId: string }
      | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore
        ? { cursor: lastPage.nextCursor!, cursorId: lastPage.nextCursorId! }
        : undefined,
    staleTime: 30_000,
  })
}

export function useCreatePortfolioUpdate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePortfolioUpdateData) =>
      portfolioUpdateRepo.createUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTFOLIO_UPDATES_KEY })
    },
  })
}

export function useDeletePortfolioUpdate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => portfolioUpdateRepo.deleteUpdate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTFOLIO_UPDATES_KEY })
    },
  })
}
